import { useAuth0 } from "@auth0/auth0-react"
import axios from "axios"
import config from "config"
import { defaultInterval } from "hardcodes"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AppMetadata, Interval, IntervalName, PageName, RegionChoice, UserMetadata } from "types/auth"
import useAccessToken from "./useAccessToken"
import { auth0UserQueryKey, useBootstrapSettled } from "./bootstrapQuery"

// The signed-in account's own profile: identity, UI preferences
// (user_metadata) and default customer (app_metadata.customerID), from rr-api
// /me/profile; preferences are written with PATCH /me/preferences, which only
// accepts the preference keys and only ever touches the caller's own account.
// (ART-3182: was rr-lambda-users /users/dev/{sub}, which let any user write
// any account's metadata.) Per-customer settings — vehicle, officer code,
// control desk — are not here: see useMySettings and the control-desk provider.
export type Profile = {
  user_id: string
  email: string | null
  name: string | null
  user_metadata: UserMetadata
  app_metadata: AppMetadata
}

const meUrl = `${config.railApiBasePath}/me`
const updateMetadataMutationKey = ["update-user-metadata"]

// While a mutation's onSettled runs it still counts as in flight itself.
export const shouldRefetchProfileAfterWrite = ({ succeeded, writesInFlight }: { succeeded: boolean; writesInFlight: number }) =>
  !succeeded || writesInFlight > 1

const useAuth0Metadata = () => {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const token = useAccessToken()
  // GET /me/bootstrap carries the profile: don't race it with a second read
  const bootstrapSettled = useBootstrapSettled()

  const userIsSuperAdmin = user?.[`https://routereports.com/roles`]?.includes("super-admin")
  const userIsAdmin = user?.[`https://routereports.com/roles`]?.includes("admin")

  const requestConfig = { headers: { Authorization: `Bearer ${token}` } }

  // get updated user metadata
  const {
    data,
    dataUpdatedAt: profileUpdatedAt,
    isLoading: isLoadingMetadata,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: auth0UserQueryKey,
    queryFn: async () => axios.get<Profile>(`${meUrl}/profile`, requestConfig).then((res) => res.data),
    enabled: !!user && !!token && bootstrapSettled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // preferences are not worth blocking the app on: one retry, then the
    // app runs on defaults (isMetadataSettled)
    retry: 1,
  })

  const userMetadata = data?.user_metadata
  const appMetadata = data?.app_metadata
  // the profile call answered (or gave up) — the app may start either way
  const isMetadataSettled = isSuccess || isError

  const { mutate: updateUserMetadata, isPending: isUpdatingMetadata } = useMutation({
    mutationKey: updateMetadataMutationKey,
    mutationFn: async (updateData: Partial<UserMetadata>) => axios.patch<Profile>(`${meUrl}/preferences`, { updateData }, requestConfig),

    // onMutate for optimistic UI update
    onMutate: async (updateData) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: auth0UserQueryKey })

      // Get the previous value
      const previousUserObj = queryClient.getQueryData<Profile>(auth0UserQueryKey)

      // Optimistically update to the new value
      // please fix typescript
      queryClient.setQueryData<any>(auth0UserQueryKey, {
        ...previousUserObj,
        user_metadata: { ...previousUserObj?.user_metadata, ...updateData },
      })

      // Return a context object with the snapshotted value
      return { previousUserObj }
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    // please fix typescript
    onError: (_err, _variables, context: any) => {
      if (context?.previousUserObj) {
        // context obj from above
        queryClient.setQueryData<Profile>(auth0UserQueryKey, context.previousUserObj)
      }
    },

    // The PATCH answers with the profile as stored (projected like GET
    // /me/profile), so a lone write needs no re-read — every read is an Auth0
    // Management API call server-side, and App writes lastOpenedAt on every
    // load. Re-read after an error, or when writes overlap: an earlier
    // response could predate a later write's optimistic update.
    onSettled: (response, error) => {
      if (shouldRefetchProfileAfterWrite({ succeeded: !error && !!response, writesInFlight: queryClient.isMutating({ mutationKey: updateMetadataMutationKey }) })) {
        queryClient.invalidateQueries({ queryKey: auth0UserQueryKey })
      } else if (response) {
        queryClient.setQueryData<Profile>(auth0UserQueryKey, response.data)
      }
    }
  })

  const updateInterval = async ({ name, value }: Interval) => {
    const newInterval = { name, value }

    // if user has no saved intervals, save this one directly
    if (!userMetadata?.intervals || !Array.isArray(userMetadata?.intervals)) {
      updateUserMetadata({ intervals: [newInterval] })
      return
    }

    // otherwise we find the matching interval and change it
    // while keeping others untouched
    const intervalAlreadyExists = userMetadata.intervals.some(
      (interval: Interval) => interval.name === newInterval.name,
    )

    const updatedIntervals = intervalAlreadyExists
      ? userMetadata.intervals.filter((interval: Interval) => interval.name !== newInterval.name)
      : userMetadata.intervals

    updatedIntervals.push(newInterval)

    updateUserMetadata({
      intervals: updatedIntervals,
    })
  }

  const getIntervalValue = (name: IntervalName) =>
    userMetadata?.intervals?.find((i: Interval) => i.name === name)?.value || defaultInterval

  const getSelectedRegionsByPage = (pageName: PageName) =>
    userMetadata?.regionChoices?.find((regionChoice: RegionChoice) => regionChoice.name === pageName)

  const updateRegionChoices = async ({ name, value }: RegionChoice) => {
    // our backend takes an array so we must normalize it in that case
    const newValue = Array.isArray(value) ? value : [value]

    const newRegionChoice: RegionChoice = { name, value: newValue }

    // if user has no saved region choices, save this one directly
    if (!userMetadata?.regionChoices || !Array.isArray(userMetadata?.regionChoices)) {
      updateUserMetadata({ regionChoices: [newRegionChoice] })
      return
    }

    // otherwise we find the matching interval and change it
    // while keeping others untouched
    const regionChoiceAlreadyExists = userMetadata?.regionChoices.some(
      (interval: RegionChoice) => interval.name === newRegionChoice.name,
    )

    const updatedRegionChoices = regionChoiceAlreadyExists
      ? userMetadata.regionChoices.filter(
          (existingChoice: RegionChoice) => existingChoice.name !== newRegionChoice.name,
        )
      : userMetadata.regionChoices

    updatedRegionChoices.push(newRegionChoice)

    updateUserMetadata({
      regionChoices: updatedRegionChoices,
    })
  }

  return {
    userIsSuperAdmin,
    userIsAdmin,
    // the whole profile and when it was last written to the cache (a fetch, a
    // preference write, or a boot snapshot — dated when that was taken)
    profile: data,
    profileUpdatedAt,
    userMetadata,
    appMetadata,
    updateInterval,
    isLoadingMetadata,
    isMetadataSettled,
    isUpdatingMetadata,
    getIntervalValue,
    updateUserMetadata,
    getSelectedRegionsByPage,
    updateRegionChoices,
  }
}

export default useAuth0Metadata
